import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Row, Col, Select, Typography, Spin, message, Empty, Table,
  Input, InputNumber, Button, Tag, Tooltip, Popconfirm, Space, Card, AutoComplete,
} from 'antd';
import {
  CheckOutlined, SaveOutlined, DeleteOutlined, CopyOutlined,
  CheckCircleFilled, DollarOutlined, FileExcelOutlined, FilePdfOutlined,
} from '@ant-design/icons';
import { ventaTransitoriaService } from '../../services/api';
import { descargarBlob } from '../../utils/descargas';
import { money } from '../../utils/format';
import { formatearMonto, parsearMonto } from '../../utils/montoInput';

const { Text } = Typography;
const { Option } = Select;

// Etiquetas del dato faltante. `id_categoria` e `id_raza` son nullable en
// `animal`, así que estos grupos existen de verdad y no son un caso teórico.
// Mismos textos que `backend/app/utils/resumen_agrupado.py`.
const SIN_CATEGORIA = 'Sin categoría';
const SIN_RAZA = 'Sin raza';

// Separador entre la categoría y su raza cuando la categoría tiene UNA sola.
// El punto medio y no un guión largo: en esta misma tabla el "—" ya significa
// "sin dato" en las columnas de monto. Mismo separador que el backend
// (`SEPARADOR_RAZA_UNICA` en resumen_agrupado.py).
const SEPARADOR_RAZA_UNICA = ' · ';

/**
 * mínimo / promedio / máximo de una lista de importes, todo null si está vacía.
 *
 * Una lista vacía NO es cero: significa que hay animales en el grupo pero
 * ninguno con precio cargado. Devolver 0 hundiría el mínimo y mostraría una
 * venta que no ocurrió.
 *
 * Espeja `stats_montos()` de `backend/app/utils/resumen_agrupado.py`.
 */
const statsMontos = (montos) => (montos.length
  ? {
    minimo:   Math.min(...montos),
    promedio: montos.reduce((a, b) => a + b, 0) / montos.length,
    maximo:   Math.max(...montos),
  }
  : { minimo: null, promedio: null, maximo: null });

/**
 * Orden de un grupo: primero el que tiene dato, después por cantidad de
 * animales descendente, y alfabético para desempatar.
 *
 * El grupo del dato faltante ("Sin raza", "Sin categoría") va SIEMPRE último,
 * sin importar cuántos animales tenga: no es una raza ni una categoría, es un
 * agujero en la carga, y ordenarlo por cantidad lo disfraza de grupo real
 * arriba de todo cuando falta cargar la mitad del remate.
 *
 * Espeja `clave_grupo()` del backend, que usa la misma regla para que el orden
 * de la pantalla, el del Excel y el del PDF sean el mismo.
 *
 * Ordena por `nombre` (el nombre PELADO) y no por `grupo` (el texto que se
 * pinta en la celda). No son lo mismo: cuando la categoría tiene una sola raza,
 * `grupo` lleva la raza pegada, así que "Sin categoría" pasa a ser
 * "Sin categoría · Sin raza" y deja de matchear el centinela — el agujero de
 * carga se ordenaría por cantidad y saldría primero en pantalla mientras el
 * Excel y el PDF lo mandan último. El backend no tiene el problema porque
 * ordena ANTES de aplanar (`aplanar_resumen` concatena después).
 *
 * Un dato de presentación no es una clave de ordenamiento.
 */
const comparadorGrupo = (sinDato) => (a, b) => {
  const fa = a.nombre === sinDato ? 1 : 0;
  const fb = b.nombre === sinDato ? 1 : 0;
  if (fa !== fb) return fa - fb;
  if (a.animales !== b.animales) return b.animales - a.animales;
  return a.nombre.localeCompare(b.nombre, 'es');
};

// La fila de la categoría va en negrita para despegarse de sus razas: con diez
// categorías abiertas, la sangría sola no alcanza para ver dónde empieza una.
//
// Decide por `esCategoria` y NO por `record.children`: una categoría con una
// sola raza no se abre (ver `resumen`), así que se quedaría sin hijos y sin
// negrita — la única fila del listado que no parece lo que es.
const resaltarSiCategoria = (contenido, record) => (
  record.esCategoria ? <strong>{contenido}</strong> : contenido
);

// Fila editable a partir de la identidad del animal (precarga).
const filaDesdeAnimal = (a) => ({
  id_animal:        a.id_animal,
  corral:           a.corral,
  cantidad_corral:  a.cantidad_corral,
  rp_animal:        a.rp_animal,
  nom_animal:       a.nom_animal,
  registro:         a.registro,
  agrupado:         a.agrupado || 0,
  nom_categoria:    a.nom_categoria,
  sexo:             a.sexo,
  nom_raza:         a.nom_raza,
  caravana:         a.caravana,
  pelo:             a.pelo,
  pct_vendido:      a.pct_vendido || 0,   // % ya vendido DEFINITIVO (venta_animal)
  // Estado editable + asiento transitorio
  id_venta_transitoria: null,
  comprador_texto:  '',
  importe_venta:    null,
  porcentaje_venta: 100,
  anotacion:        '',
  dirty:            false,
});

/**
 * Filas del corral en el MISMO orden en que se ven en la grilla.
 *
 * `Object.values(filas)` itera por clave numérica, o sea por `id_animal`, y la
 * grilla ordena por corral y después por RP. Cuando en un corral hay más de un
 * animal con comprador cargado, "el 1º" del mensaje y "el 1º" que el operador
 * ve arriba pueden ser animales distintos — y se propagan los datos del que no
 * era.
 */
const filasDelCorral = (filas, corral) => Object.values(filas)
  .filter((f) => f.agrupado && f.corral === corral && (f.pct_vendido || 0) < 100)
  .sort((a, b) => String(a.rp_animal || '').localeCompare(String(b.rp_animal || '')));

// Campos de la barra de filtros rápidos, en el orden en que se muestran.
const CAMPOS_FILTRO = [
  { key: 'corral',        label: 'N° Corral' },
  { key: 'nom_raza',      label: 'Raza' },
  { key: 'nom_categoria', label: 'Categoría' },
  { key: 'caravana',      label: 'Caravana' },
  { key: 'pelo',          label: 'Pelo' },
];

const FILTROS_VACIOS = Object.fromEntries(CAMPOS_FILTRO.map((c) => [c.key, '']));

/**
 * Normaliza para comparar: minúsculas y SIN acentos.
 *
 * Sin sacar los acentos, buscar "prenada" no encuentra "Vaquillas Preñadas" y
 * el operador concluye que el filtro no anda. Nadie escribe la eñe con una
 * mano en el teclado y la otra en la planilla.
 */
const normalizar = (v) => String(v ?? '')
  .normalize('NFD').replace(/\p{Diacritic}/gu, '')  // marcas de acento
  .toLowerCase().trim();

/** Pares [campo, valor] de los filtros con algo escrito. */
const filtrosActivos = (filtros) =>
  Object.entries(filtros).filter(([, v]) => String(v).trim());

/**
 * ¿La fila coincide con todos los filtros escritos? Con Y, no con O: raza
 * "braford" + categoría "toros" deja los toros braford, no la suma de ambos.
 *
 * Una sola definición porque la usan el filtrado Y el conteo de los que se
 * ocultan por estar cargados; con la condición escrita dos veces, el número
 * del cartel deja de coincidir con las filas de la tabla en el primer retoque.
 */
const coincide = (fila, activos) => activos.every(([campo, valor]) => {
  // El corral se compara EXACTO. Es un identificador, no un texto: con
  // `includes`, escribir "2" devuelve 2, 12, 20 a 29, 32, 102… y en un remate
  // de 200 corrales el filtro que más se usa —el que se tipea mientras el
  // martillero canta el lote— trae un puñado de corrales que no son.
  if (campo === 'corral') return String(fila.corral ?? '') === String(valor).trim();
  return normalizar(fila[campo]).includes(normalizar(valor));
});

const conAsiento = (fila, asiento) => ({
  ...fila,
  id_venta_transitoria: asiento.id_venta_transitoria,
  comprador_texto:  asiento.comprador_texto || '',
  importe_venta:    asiento.importe_venta ?? null,
  porcentaje_venta: asiento.porcentaje_venta ?? 100,
  anotacion:        asiento.anotacion || '',
  dirty:            false,
});

// Grid de venta transitoria. Recibe la lista de eventos ya cargada por el
// contenedor (VentaAnimales) para no re-fetchearla; maneja su propio evento.
const GrillaTransitoria = ({ eventos = [], loadingEventos = false }) => {
  const [eventoId, setEventoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savingCorral, setSavingCorral] = useState(null);
  const [exportando, setExportando] = useState(null);
  const [propagados, setPropagados] = useState(() => new Set());
  const [colapsadas, setColapsadas] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);

  // Número de carga: sólo la ÚLTIMA puede escribir en la grilla.
  //
  // Dos cargas en vuelo se pisan: el operador elige el evento A (grilla grande,
  // lenta), se da cuenta del error y elige el B; si B contesta primero y A
  // después, gana A. El Select dice "B" y la grilla tiene los animales de A, y
  // el asiento termina guardado en el evento equivocado porque `crear_asiento`
  // deriva el id_evento DEL ANIMAL.
  //
  // Un flag booleano compartido NO alcanza: React corre el cleanup del efecto
  // anterior y enseguida el efecto nuevo, que lo volvería a poner en true justo
  // a tiempo para que la respuesta vieja pase el guard. Con un contador, cada
  // carga se queda con su número y la que llega tarde ya no coincide.
  const generacion = useRef(0);

  const precargar = useCallback(async () => {
    // El incremento va ANTES del early return: si el operador limpia el Select
    // con una precarga en vuelo, esa respuesta tiene que quedar invalidada
    // igual. Si no, vuelve y repuebla la grilla que se acaba de vaciar.
    const miCarga = ++generacion.current;
    if (!eventoId) { setFilas({}); setFiltros(FILTROS_VACIOS); setColapsadas([]); return; }
    setLoading(true);
    try {
      const res = await ventaTransitoriaService.precargaEvento(eventoId);
      if (miCarga !== generacion.current) return;
      const { animales = [], asientos = [] } = res.data || {};
      const asientoPorAnimal = {};
      asientos.forEach((a) => { asientoPorAnimal[a.id_animal] = a; });
      const map = {};
      animales.forEach((a) => {
        const base = filaDesdeAnimal(a);
        map[a.id_animal] = asientoPorAnimal[a.id_animal]
          ? conAsiento(base, asientoPorAnimal[a.id_animal])
          : base;
      });
      setFilas(map);
      setPropagados(new Set());
      // El filtro es del evento anterior: con "corral 12" puesto, el evento
      // nuevo entra a una grilla que muestra sólo su corral 12 sin cargar, o
      // vacía si no tiene. Se limpia con el resto del estado de pantalla.
      setFiltros(FILTROS_VACIOS);
      // Las claves de `colapsadas` son NOMBRES de categoría, no ids: sin este
      // reset, colapsar "Toros" en un remate deja "Toros" cerrada en el
      // siguiente sin que nadie la haya tocado.
      setColapsadas([]);
    } catch (err) {
      if (miCarga !== generacion.current) return;
      // Vaciar la grilla NO es cosmético: `crear_asiento` deriva el id_evento
      // DEL ANIMAL, no de lo que manda el cliente. Si falla la carga del
      // evento B y quedan en pantalla los animales del A, el operador escribe
      // un comprador, guarda, y el asiento se registra en el evento A — en
      // silencio y en pleno remate. Mejor una grilla vacía que una que miente.
      setFilas({});
      setPropagados(new Set());
      setFiltros(FILTROS_VACIOS);
      setColapsadas([]);
      message.error(err?.response?.data?.error || 'Error al cargar la grilla del evento');
    } finally {
      // Si ya arrancó una carga más nueva, el spinner es de ELLA: apagarlo acá
      // dejaría la grilla sin indicación mientras la otra sigue en vuelo.
      if (miCarga === generacion.current) setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { precargar(); }, [precargar]);

  const setCampo = (id, campo, valor) => {
    setFilas((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor, dirty: true } }));
  };

  const guardar = async (id) => {
    const f = filas[id];
    if (!f || !(f.comprador_texto || '').trim()) { message.warning('Falta el comprador'); return; }
    setSavingId(id);
    try {
      const res = await ventaTransitoriaService.crear({
        id_animal: id,
        comprador_texto: f.comprador_texto.trim(),
        porcentaje_venta: f.porcentaje_venta,
        importe_venta: f.importe_venta,
        anotacion: (f.anotacion || '').trim() || null,
      });
      setFilas((prev) => ({ ...prev, [id]: conAsiento(prev[id], res.data) }));
      message.success('Asiento guardado');
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al guardar');
    } finally { setSavingId(null); }
  };

  const actualizar = async (id) => {
    const f = filas[id];
    if (!f?.id_venta_transitoria) return;
    if (!(f.comprador_texto || '').trim()) { message.warning('El comprador no puede quedar vacío'); return; }
    setSavingId(id);
    try {
      const res = await ventaTransitoriaService.editar(f.id_venta_transitoria, {
        comprador_texto: f.comprador_texto.trim(),
        porcentaje_venta: f.porcentaje_venta,
        importe_venta: f.importe_venta,
        anotacion: (f.anotacion || '').trim() || null,
      });
      setFilas((prev) => ({ ...prev, [id]: conAsiento(prev[id], res.data) }));
      message.success('Asiento actualizado');
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al actualizar');
    } finally { setSavingId(null); }
  };

  const eliminar = async (id) => {
    const f = filas[id];
    if (!f?.id_venta_transitoria) return;
    setSavingId(id);
    try {
      await ventaTransitoriaService.eliminar(f.id_venta_transitoria);
      setFilas((prev) => ({
        ...prev,
        [id]: { ...prev[id], id_venta_transitoria: null, comprador_texto: '', importe_venta: null, porcentaje_venta: 100, anotacion: '', dirty: false },
      }));
      message.success('Asiento eliminado');
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al eliminar');
    } finally { setSavingId(null); }
  };

  // Aplica (propaga) los datos del 1º animal cargado al resto del corral. Es
  // VISIBLE: el usuario ve que se llenaron todas las filas. Luego el botón muta
  // a "Guardar todo el corral" (ver columna de acción).
  //
  // El importe se copia TAL CUAL a cada animal, sin dividir por la cantidad del
  // corral, y eso es correcto: cuando un corral se vende como lote, "el que
  // gana la puja se lleva todo el corral y tiene que pagar CADA ANIMAL lo que
  // fijó en la puja". El precio es siempre POR CABEZA, nunca del lote —
  // dividirlo haría que el resumen por categoría reporte una fracción de lo
  // que realmente entra.
  const propagarCorral = (corral) => {
    const rows = filasDelCorral(filas, corral);
    // La UI ya no ofrece el botón en este caso; el corte va igual acá porque
    // es la función la que tiene que garantizar la regla, no el botón.
    if (rows.some((f) => f.id_venta_transitoria != null)) {
      message.warning('Este corral ya tiene ventas guardadas — cargá los que faltan a mano');
      return;
    }
    const src = rows.find((f) => (f.comprador_texto || '').trim());
    if (!src) { message.warning('Cargá el comprador del primer animal del corral'); return; }
    // Sólo se llenan las filas VACÍAS. Antes se pisaba todo menos `src`, y el
    // animal que ya tenía su propio comprador —incluso uno ya GUARDADO— se
    // quedaba con los datos del primero; después "Guardar todo el corral" hacía
    // el PUT y la venta anterior desaparecía de la base, con un toast de éxito
    // y sin que ningún reporte lo delate. Es el mismo criterio que guardarCorral
    // ya documenta: "los que ya lo tienen, guardan lo suyo".
    // Para cambiarle el comprador a uno ya cargado está la edición fila por fila.
    const aLlenar = rows.filter(
      (f) => f.id_animal !== src.id_animal
        && f.id_venta_transitoria == null
        && !(f.comprador_texto || '').trim(),
    );
    const conservados = rows.length - aLlenar.length - 1;

    setFilas((prev) => {
      const next = { ...prev };
      aLlenar.forEach((f) => {
        next[f.id_animal] = {
          ...prev[f.id_animal],
          comprador_texto:  src.comprador_texto,
          porcentaje_venta: src.porcentaje_venta,
          importe_venta:    src.importe_venta,
          dirty: true,
        };
      });
      return next;
    });
    setPropagados((prev) => new Set(prev).add(corral));
    message.success(
      `Datos aplicados a ${aLlenar.length} animal${aLlenar.length === 1 ? '' : 'es'}`
      + (conservados > 0
        ? ` — ${conservados} conservó(aron) su comprador. Revisá y guardá.`
        : ' — revisá y guardá.')
    );
  };

  // Guarda los animales (no vendidos) del corral de una. Los que NO tienen
  // comprador propio heredan el del 1º cargado; los que ya lo tienen, guardan lo
  // suyo. Así podés guardar de a 1 (botón por fila) o todo el corral junto.
  //
  // EXCEPTO si el corral ya tiene ventas guardadas: ahí no hereda nadie y sólo
  // se guardan los que tengan comprador propio. Heredar TAMBIÉN es propagar, y
  // en un corral mixto el "1º" es el primero por RP —que puede ser una venta
  // individual vieja— y no el que se acaba de cargar para el lote.
  const guardarCorral = async (corral) => {
    const todas = filasDelCorral(filas, corral);
    const yaGuardado = todas.some((f) => f.id_venta_transitoria != null);
    const rows = yaGuardado
      ? todas.filter((f) => (f.comprador_texto || '').trim())
      : todas;
    const src = rows.find((f) => (f.comprador_texto || '').trim());
    if (!src) { message.warning('Cargá el comprador del primer animal del corral'); return; }
    setSavingCorral(corral);
    try {
      // allSettled (NO all): al guardar N en paralelo el éxito PARCIAL es lo
      // normal. Aplicamos los que salieron OK al estado (para que reciban su
      // id_venta_transitoria) aunque alguno falle, y reportamos los fallidos.
      const settled = await Promise.allSettled(rows.map(async (f) => {
        const base = (f.comprador_texto || '').trim() ? f : src;
        const payload = {
          comprador_texto:  base.comprador_texto.trim(),
          porcentaje_venta: base.porcentaje_venta,
          importe_venta:    base.importe_venta,
          anotacion:        (f.anotacion || '').trim() || null,
        };
        const res = f.id_venta_transitoria
          ? await ventaTransitoriaService.editar(f.id_venta_transitoria, payload)
          : await ventaTransitoriaService.crear({ id_animal: f.id_animal, ...payload });
        return [f.id_animal, res.data];
      }));
      const oks = settled.filter((s) => s.status === 'fulfilled').map((s) => s.value);
      const fallidos = settled.length - oks.length;
      if (oks.length) {
        setFilas((prev) => {
          const next = { ...prev };
          oks.forEach(([id, asiento]) => { next[id] = conAsiento(next[id], asiento); });
          return next;
        });
        message.success(`Corral guardado (${oks.length} animal${oks.length === 1 ? '' : 'es'})`);
      }
      if (fallidos) {
        // Con el MOTIVO del backend, no un "reintentá" a secas: si el rechazo
        // es de validación (ej. "porcentaje_venta debe ser entre 1 y 100"),
        // reintentar falla igual para siempre y el operador se queda pedaleando
        // en pleno remate. Se muestra el primer motivo distinto; si todos
        // fallan por lo mismo, alcanza con uno.
        const motivo = settled
          .filter((s) => s.status === 'rejected')
          .map((s) => s.reason?.response?.data?.error)
          .find(Boolean);
        message.error(
          `${fallidos} no se pudo(ieron) guardar${motivo ? `: ${motivo}` : ' — reintentá'}`
        );
      }
    } catch (err) {
      // `allSettled` no rechaza, así que esto sólo salta ante un error
      // inesperado (por ejemplo dentro del updater de setFilas). Sin el catch,
      // el operador se queda con el spinner apagándose y CERO feedback — y es
      // justo la acción masiva en la que más se apoya durante el remate.
      message.error(err?.response?.data?.error || 'Error al guardar el corral');
    } finally {
      setSavingCorral(null);
    }
  };

  // Sólo lo que YA tiene asiento guardado. Se deriva de `filas`, que es lo que
  // la grilla viene editando, así que la card se actualiza sola al guardar
  // cada venta — sin volver a pedirle nada al servidor en medio del remate.
  const ventasCargadas = useMemo(
    () => Object.values(filas)
      .filter((f) => f.id_venta_transitoria != null)
      .sort((a, b) => {
        const ca = a.corral ?? Infinity, cb = b.corral ?? Infinity;
        if (ca !== cb) return ca - cb;
        return String(a.rp_animal || '').localeCompare(String(b.rp_animal || ''));
      }),
    [filas],
  );

  // Mínimo / promedio / máximo por categoría y, subagrupado adentro, por raza.
  //
  // POR QUÉ POR RAZA: el valor de mercado se expresa por raza. Un promedio que
  // mezcla Angus con Brangus no representa a ninguno de los dos — describe un
  // animal que no existe. La fila de la categoría queda igual que antes (se
  // calcula sobre TODOS sus montos, no sumando las razas), la raza se abre
  // debajo.
  //
  // Todo sobre el precio llevado al 100% del animal: `importe_venta` es lo que
  // paga ESE comprador por SU parte, así que un animal vendido al 50% en
  // $5.000.000 vale $10.000.000. Sin normalizar, una venta parcial hunde el
  // mínimo y el promedio de la categoría y parece que ese animal se remató
  // barato.
  //
  // `total` ES LA EXCEPCIÓN: suma el importe REAL, lo que se cobró, no el
  // llevado al 100%. Mínimo/promedio/máximo describen el valor de mercado;
  // el total describe la plata que entró. Pedido del usuario, 2026-10-07.
  //
  // Debe coincidir con `_importe_pleno` / `_resumen_por_categoria` del backend
  // (venta_transitoria_routes.py), que es lo que sale en el Excel y el PDF.
  const resumen = useMemo(() => {
    const porCat = new Map();
    ventasCargadas.forEach((f) => {
      const cat = f.nom_categoria || SIN_CATEGORIA;
      const raza = f.nom_raza || SIN_RAZA;
      if (!porCat.has(cat)) porCat.set(cat, { animales: 0, montos: [], total: 0, razas: new Map() });
      const d = porCat.get(cat);
      d.animales += 1;
      if (!d.razas.has(raza)) d.razas.set(raza, { animales: 0, montos: [], total: 0 });
      const r = d.razas.get(raza);
      r.animales += 1;
      if (f.importe_venta != null) {
        d.total += Number(f.importe_venta);
        r.total += Number(f.importe_venta);
      }
      // Sin precio cargado el animal se cuenta como vendido pero no entra en
      // los montos: el asiento existe (hay comprador), lo que falta es el
      // precio, y meterlo como 0 hundiría el mínimo.
      if (f.importe_venta != null) {
        const pct = Number(f.porcentaje_venta) || 100;
        const pleno = pct > 0 ? (Number(f.importe_venta) * 100) / pct : Number(f.importe_venta);
        d.montos.push(pleno);
        r.montos.push(pleno);
      }
    });
    return [...porCat.entries()]
      .map(([cat, d]) => {
        // UNA SOLA RAZA NO SE ABRE: la fila hija repetiría número por número a
        // la madre. Lo único que aporta es el NOMBRE de la raza, y eso va
        // pegado a la categoría. Hay remates enteros con una sola raza (Pilagá
        // en Mercedes): sin esta regla ese resumen duplica todas sus filas para
        // no decir nada nuevo. Misma regla que `aplanar_resumen()` del backend,
        // que es lo que sale en el Excel y el PDF.
        const razas = [...d.razas.entries()]
          .map(([raza, r]) => ({
            // La clave lleva la categoría adelante: la misma raza está en
            // varias categorías y antd necesita claves únicas en todo el árbol.
            key:      cat + ' :: ' + raza,
            // `nombre` es por lo que se ORDENA, `grupo` es lo que se PINTA. En
            // la raza coinciden; en la categoría no (ver `comparadorGrupo`).
            nombre:   raza,
            grupo:    raza,
            animales: r.animales,
            total:    r.total,
            ...statsMontos(r.montos),
          }))
          .sort(comparadorGrupo(SIN_RAZA));
        const unica = razas.length === 1 ? razas[0].grupo : null;
        return {
          key:         cat,
          // El nombre PELADO manda en el orden; la raza pegada es sólo texto.
          nombre:      cat,
          grupo:       unica ? cat + SEPARADOR_RAZA_UNICA + unica : cat,
          esCategoria: true,
          animales:    d.animales,
          total:       d.total,
          ...statsMontos(d.montos),
          // `children: undefined` y no `[]`: con un array vacío antd igual
          // dibuja la flechita de desplegar y no despliega nada.
          ...(unica ? {} : { children: razas }),
        };
      })
      .sort(comparadorGrupo(SIN_CATEGORIA));
  }, [ventasCargadas]);

  // El total general: suma de los totales por categoría, igual que
  // `fila_total()` del backend, que es lo que cierra el Excel y el PDF.
  const totalGeneral = useMemo(() => ({
    animales: resumen.reduce((acc, c) => acc + c.animales, 0),
    total:    resumen.reduce((acc, c) => acc + (c.total || 0), 0),
  }), [resumen]);

  // Se guardan las categorías COLAPSADAS, no las abiertas. El resumen se
  // recalcula con cada venta que se carga, y una categoría nueva que aparece a
  // mitad del remate tiene que salir abierta sin que nadie la toque: si
  // guardáramos las abiertas, aparecería cerrada y el operador ve una fila que
  // "no tiene razas".
  const clavesAbiertas = useMemo(
    () => resumen.filter((r) => r.children).map((r) => r.key)
      .filter((k) => !colapsadas.includes(k)),
    [resumen, colapsadas],
  );

  const exportar = async (formato) => {
    setExportando(formato);
    try {
      const res = formato === 'excel'
        ? await ventaTransitoriaService.exportarExcel(eventoId)
        : await ventaTransitoriaService.exportarPdf(eventoId);
      descargarBlob(res, `ventas_transitorias_${eventoId}.${formato === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar');
    } finally {
      setExportando(null);
    }
  };

  // Filtro rápido sobre la grilla. Todos los campos combinan con Y: escribir
  // raza "braford" y categoría "toros" deja los toros braford, no la suma.
  //
  // Con el filtro activo se ocultan además los que YA tienen asiento guardado:
  // el filtro se usa para encontrar lo que FALTA cargar, y las filas ya hechas
  // sólo tapan las pendientes. Sin filtro se ve todo, que es como se revisa.
  const filasFiltradas = useMemo(() => {
    const activos = filtrosActivos(filtros);
    if (!activos.length) return filas;
    const out = {};
    Object.entries(filas).forEach(([id, f]) => {
      if (f.id_venta_transitoria == null && coincide(f, activos)) out[id] = f;
    });
    return out;
  }, [filas, filtros]);

  // Cuántos quedaron afuera POR ESTAR CARGADOS (no por no coincidir): es lo que
  // se le informa al operador, y tiene que ser sobre los que matchean el
  // filtro, no sobre el evento entero.
  const ocultosPorCargado = useMemo(() => {
    const activos = filtrosActivos(filtros);
    if (!activos.length) return 0;
    return Object.values(filas)
      .filter((f) => f.id_venta_transitoria != null && coincide(f, activos))
      .length;
  }, [filas, filtros]);

  // Opciones del autocompletado: los valores que EXISTEN en este evento, no
  // toda la tabla `raza` de la base. Ofrecer una raza que no está en el remate
  // es ofrecer un filtro que devuelve cero — y encima obliga a un round-trip
  // para traer algo que ya está en memoria.
  const opcionesFiltro = useMemo(() => {
    const sets = Object.fromEntries(CAMPOS_FILTRO.map((c) => [c.key, new Set()]));
    Object.values(filas).forEach((f) => {
      CAMPOS_FILTRO.forEach(({ key }) => {
        const v = f[key];
        if (v !== null && v !== undefined && String(v).trim()) sets[key].add(String(v));
      });
    });
    return Object.fromEntries(
      Object.entries(sets).map(([k, set]) => [
        k,
        [...set]
          .sort((a, b) => (k === 'corral'
            ? Number(a) - Number(b)
            : a.localeCompare(b, 'es')))
          .map((v) => ({ value: v })),
      ]),
    );
  }, [filas]);

  const hayFiltro = Object.values(filtros).some((v) => String(v).trim());
  const totalFiltrado = Object.keys(filasFiltradas).length;

  // Sobre las FILTRADAS: este contador decide si la fila esconde su número de
  // corral (porque la cabecera del grupo ya lo muestra). Con el conteo sin
  // filtrar, un corral que queda con UN solo animal se dibuja como fila suelta
  // —sin cabecera— pero el contador seguía diciendo 5 y la celda salía vacía:
  // la fila perdía justo el dato con el que se canta el lote.
  const corralCount = useMemo(() => {
    const m = {};
    Object.values(filasFiltradas).forEach((f) => {
      if (f.agrupado && f.corral != null) m[f.corral] = (m[f.corral] || 0) + 1;
    });
    return m;
  }, [filasFiltradas]);

  // Sobre las FILTRADAS: el árbol se arma después de filtrar, así un corral
  // agrupado aparece con los hijos que pasan el filtro, y si no pasa ninguno
  // no aparece. Filtrar el árbol ya armado dejaría corrales vacíos colgando.
  const treeData = useMemo(() => {
    const arr = Object.values(filasFiltradas).sort((a, b) => {
      const ca = a.corral ?? Infinity, cb = b.corral ?? Infinity;
      if (ca !== cb) return ca - cb;
      return String(a.rp_animal || '').localeCompare(String(b.rp_animal || ''));
    });
    const unidades = [];
    const idxCorral = new Map();
    for (const f of arr) {
      if (f.agrupado && f.corral != null) {
        if (idxCorral.has(f.corral)) unidades[idxCorral.get(f.corral)].children.push(f);
        else { idxCorral.set(f.corral, unidades.length); unidades.push({ isGroup: true, corral: f.corral, children: [f] }); }
      } else unidades.push(f);
    }
    return unidades.map((u) => (u.isGroup && u.children.length === 1 ? u.children[0] : u));
  }, [filasFiltradas]);

  const total = Object.keys(filas).length;
  const cargados = useMemo(
    () => Object.values(filas).filter((f) => f.id_venta_transitoria != null).length,
    [filas],
  );

  const columns = [
    {
      title: 'Corral', dataIndex: 'corral', width: 68, align: 'center',
      render: (_, r) => {
        if (r.isGroup) return <Space size={4}><Text strong>{r.corral}</Text><Tag color="gold" style={{ margin: 0 }}>{r.children.length}</Tag></Space>;
        const enGrupo = r.agrupado && corralCount[r.corral] > 1;
        return enGrupo ? '' : (r.corral ?? '—');
      },
    },
    {
      title: 'RP', dataIndex: 'rp_animal', width: 92,
      render: (_, r) => r.isGroup ? null : (
        <Space size={4}>
          {r.id_venta_transitoria != null && <CheckCircleFilled style={{ color: '#52c41a' }} />}
          <span>{r.rp_animal}</span>
        </Space>
      ),
    },
    {
      title: 'Animal', dataIndex: 'nom_animal',
      render: (_, r) => {
        if (r.isGroup) {
          const kids = r.children || [];
          const vendidos = kids.filter((c) => (c.pct_vendido || 0) >= 100).length;
          return (
            <Space size={4}>
              <Text type="secondary">Corral Agrupado</Text>
              {kids.length > 0 && vendidos === kids.length && <Tag color="red" style={{ margin: 0 }}>Vendido</Tag>}
              {vendidos > 0 && vendidos < kids.length && <Tag color="orange" style={{ margin: 0 }}>{vendidos}/{kids.length} vendidos</Tag>}
            </Space>
          );
        }
        const vendido = (r.pct_vendido || 0) >= 100;
        const parcial = (r.pct_vendido || 0) > 0 && (r.pct_vendido || 0) < 100;
        return (
          <Space size={4}>
            <Text delete={vendido}>{r.nom_animal}</Text>
            {vendido && <Tag color="red" style={{ margin: 0 }}>Vendido</Tag>}
            {parcial && <Tag color="orange" style={{ margin: 0 }}>{r.pct_vendido}%</Tag>}
          </Space>
        );
      },
    },
    {
      // Sólo lectura: viene del animal, acá no se edita. Sirve para confirmar
      // contra el catálogo que se está cargando el lote que se está rematando.
      // En la fila de un corral agrupado no va: el rótulo "Corral Agrupado" ya
      // ocupa esa fila y la categoría se ve en cada hijo.
      title: 'Categoría', dataIndex: 'nom_categoria', width: 170, ellipsis: true,
      render: (v, r) => (r.isGroup ? null : (
        <Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Text>
      )),
    },
    {
      title: 'Comprador', dataIndex: 'comprador_texto', width: 190,
      render: (_, r) => {
        if (r.isGroup) return null;
        const vendido = (r.pct_vendido || 0) >= 100;
        return (
          <Input
            size="small" placeholder="Comprador..." disabled={vendido}
            value={r.comprador_texto}
            onChange={(e) => setCampo(r.id_animal, 'comprador_texto', e.target.value)}
          />
        );
      },
    },
    {
      title: '%', dataIndex: 'porcentaje_venta', width: 72,
      render: (_, r) => {
        if (r.isGroup) return null;
        const vendido = (r.pct_vendido || 0) >= 100;
        return (
          <InputNumber
            size="small" min={1} max={100} controls={false} style={{ width: '100%' }} disabled={vendido}
            value={r.porcentaje_venta}
            onChange={(v) => setCampo(r.id_animal, 'porcentaje_venta', v)}
          />
        );
      },
    },
    {
      // 160 y no 128: "$ 12.000.000" con los separadores necesita más lugar, y
      // un precio a medio ver es exactamente lo que este cambio vino a evitar.
      title: 'Precio', dataIndex: 'importe_venta', width: 160,
      render: (_, r) => {
        if (r.isGroup) return null;
        const vendido = (r.pct_vendido || 0) >= 100;
        // `min={1}` y no 0: un animal no se remata en cero. Con min 0 el
        // operador podía tipear un 0, el backend lo guardaba como NULL —caía
        // en el falsy de `or None`— y al releer la fila el precio no estaba,
        // sin ningún aviso. Ahora el backend lo rechaza con un mensaje, y esto
        // evita que se llegue a intentar.
        // Vaciar el campo sigue siendo válido: es "todavía no sé el precio".
        //
        // Va acá arriba y NO como `{/* */}` adentro del `return (`: ahí el
        // parser ve la llave y espera un objeto literal, no hijos de JSX. Ese
        // error de sintaxis rompió el build de Cloudflare durante días.
        return (
          <InputNumber
            size="small" min={1} prefix="$" controls={false} style={{ width: '100%' }} disabled={vendido}
            placeholder="Precio"
            value={r.importe_venta}
            onChange={(v) => setCampo(r.id_animal, 'importe_venta', v)}
            formatter={formatearMonto}
            parser={parsearMonto}
          />
        );
      },
    },
    {
      title: 'Nota', dataIndex: 'anotacion', width: 140,
      render: (_, r) => {
        if (r.isGroup) return null;
        const vendido = (r.pct_vendido || 0) >= 100;
        return (
          <Input
            size="small" placeholder="Nota..." disabled={vendido}
            value={r.anotacion}
            onChange={(e) => setCampo(r.id_animal, 'anotacion', e.target.value)}
          />
        );
      },
    },
    {
      title: '', key: 'accion', width: 104, align: 'center',
      render: (_, r) => {
        // Grupo: 1º "Propagar" (aplica el 1º a todo el corral, VISIBLE); una vez
        // propagado, el mismo botón muta a "Guardar todo el corral".
        if (r.isGroup) {
          // Sobre el corral COMPLETO y no sobre `r.children`, que con filtro
          // activo son sólo las filas visibles: propagarCorral y guardarCorral
          // trabajan sobre todo el corral, así que el botón tiene que decidir
          // con lo mismo. Si no, el filtro esconde las ventas ya cargadas,
          // `yaGuardado` da false y se ofrece un "Propagar" que la función va a
          // rechazar.
          const delCorral = filasDelCorral(filas, r.corral);
          const hayComprador = delCorral.some((c) => (c.comprador_texto || '').trim());
          // Con una venta YA GUARDADA en el corral, propagar deja de tener un
          // "primero" claro: el que manda pasa a ser el primero por RP, que
          // puede ser una venta individual vieja y no la que se acaba de
          // cargar para el lote. Se corta acá y se carga a mano — son pocos
          // animales y la alternativa es llenar el corral con el precio
          // equivocado.
          const yaGuardado = delCorral.some((c) => c.id_venta_transitoria != null);
          // Con ventas ya guardadas se saltea el paso de propagar y se pasa
          // directo a "Guardar todo": si sólo se deshabilitara el botón, el
          // corral nunca entraría en `propagados` y el operador perdería
          // también el guardado masivo, que sigue siendo útil para bajar de
          // una lo que cargó a mano.
          if (!propagados.has(r.corral) && !yaGuardado) {
            return (
              <Tooltip title="Aplicar los datos del 1º a TODO el corral (también a los que el filtro oculta)">
                <Button size="small" icon={<CopyOutlined />} disabled={!hayComprador} onClick={() => propagarCorral(r.corral)} />
              </Tooltip>
            );
          }
          return (
            <Tooltip title={yaGuardado
              ? 'Guardar los que tengan comprador cargado (este corral ya tiene ventas guardadas)'
              : 'Guardar todo el corral'}>
              <Button type="primary" size="small" icon={<CheckOutlined />} loading={savingCorral === r.corral} onClick={() => guardarCorral(r.corral)} />
            </Tooltip>
          );
        }
        const vendido = (r.pct_vendido || 0) >= 100;
        if (vendido) {
          return (
            <Tooltip title="Vendido (definitivo)">
              <Button size="small" type="text" disabled icon={<DollarOutlined style={{ color: '#cf1322' }} />} />
            </Tooltip>
          );
        }
        const guardado = r.id_venta_transitoria != null;
        return (
          <Space size={2}>
            {!guardado ? (
              <Tooltip title="Guardar">
                <Button type="primary" size="small" icon={<CheckOutlined />} loading={savingId === r.id_animal}
                  disabled={!(r.comprador_texto || '').trim()} onClick={() => guardar(r.id_animal)} />
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Actualizar">
                  <Button size="small" icon={<SaveOutlined />} loading={savingId === r.id_animal}
                    disabled={!r.dirty} onClick={() => actualizar(r.id_animal)} />
                </Tooltip>
                <Popconfirm title="¿Eliminar asiento?" okText="Eliminar" cancelText="Cancelar"
                  okButtonProps={{ danger: true }} onConfirm={() => eliminar(r.id_animal)}>
                  <Tooltip title="Borrar"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
                </Popconfirm>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Select
            loading={loadingEventos}
            placeholder="Seleccioná un evento..."
            value={eventoId}
            onChange={setEventoId}
            style={{ minWidth: 300 }}
            allowClear
          >
            {eventos.map((e) => (
              <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>
            ))}
          </Select>
        </Col>
        {eventoId && total > 0 && (
          <Col><Text type="secondary">{cargados} / {total} cargados</Text></Col>
        )}
      </Row>

      {/* ── Resumen por categoría + exports ── */}
      {!loading && eventoId && total > 0 && (
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title={<span><DollarOutlined style={{ marginRight: 8 }} />Resumen de ventas por categoría</span>}
          extra={
            <Space>
              <Button
                size="small"
                icon={<FileExcelOutlined />}
                loading={exportando === 'excel'}
                disabled={!ventasCargadas.length}
                onClick={() => exportar('excel')}
              >
                Excel
              </Button>
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                loading={exportando === 'pdf'}
                disabled={!ventasCargadas.length}
                onClick={() => exportar('pdf')}
              >
                PDF
              </Button>
            </Space>
          }
        >
          <Table
            scroll={{ x: 'max-content' }}
            dataSource={resumen}
            rowKey="key"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Todavía no cargaste ninguna venta' }}
            expandable={{
              expandedRowKeys: clavesAbiertas,
              // Sólo las filas DESPLEGABLES entran en `colapsadas`. Sin el
              // filtro por `children`, colapsar una categoría marcaba también
              // a todas las de raza única — que ni siquiera se pueden abrir —
              // y el día que a una de esas le entra una segunda raza aparece
              // cerrada. Es exactamente el caso que el comentario de `resumen`
              // dice que no puede pasar.
              onExpandedRowsChange: (abiertas) => setColapsadas(
                resumen.filter((r) => r.children).map((r) => r.key)
                  .filter((k) => !abiertas.includes(k)),
              ),
            }}
            columns={[
              {
                title: 'Categoría / Raza',
                dataIndex: 'grupo',
                width: 220,
                ellipsis: true,
                render: resaltarSiCategoria,
              },
              {
                title: 'Animales',
                dataIndex: 'animales',
                align: 'right',
                width: 90,
                render: resaltarSiCategoria,
              },
              {
                title: 'Mínimo',
                dataIndex: 'minimo',
                align: 'right',
                width: 130,
                render: (v, r) => resaltarSiCategoria(money(v), r),
              },
              {
                title: 'Promedio',
                dataIndex: 'promedio',
                align: 'right',
                width: 130,
                render: (v, r) => resaltarSiCategoria(money(v), r),
              },
              {
                title: 'Máximo',
                dataIndex: 'maximo',
                align: 'right',
                width: 130,
                render: (v, r) => resaltarSiCategoria(money(v), r),
              },
              {
                title: 'Total vendido',
                dataIndex: 'total',
                align: 'right',
                width: 140,
                render: (v, r) => resaltarSiCategoria(money(v), r),
              },
            ]}
            /* La fila de cierre, fija abajo: `summary` y no una fila más del
               dataSource porque no es una categoría —no se despliega, no se
               ordena, no se colapsa— y antd la dibuja aparte del árbol. */
            summary={() => (resumen.length ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><Text strong>Total del remate</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><Text strong>{totalGeneral.animales}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">—</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">—</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">—</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right"><Text strong>{money(totalGeneral.total)}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            ) : null)}
          />
          {/* El monto que se compara NO es el que figura en la fila de abajo
              cuando la venta es parcial. Decirlo acá evita que alguien lea el
              promedio y no le cierre contra el detalle. */}
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Mínimo, promedio y máximo llevados al 100% del animal: una venta al 50% en $5.000.000 cuenta como $10.000.000.
            {' '}El total vendido es lo cobrado tal cual se cargó, sin llevar al 100%.
            {' '}Cada categoría se abre por raza: el valor de mercado se expresa por raza, y un
            promedio que mezcla razas no describe a ninguna.
          </Text>
        </Card>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>}
      {!loading && !eventoId && <Empty description="Seleccioná un evento para cargar las ventas del remate" />}
      {!loading && eventoId && total === 0 && <Empty description="No hay animales en este evento" />}

      {/* ── Filtros rápidos ──
          Pegados a la tabla y no dentro de una Card: son un control DE la
          grilla, y separarlos en su propio bloque los aleja de lo que filtran.
          Todos combinan con Y. */}
      {!loading && eventoId && total > 0 && (
        <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 8 }}>
          {CAMPOS_FILTRO.map(({ key, label }) => (
            <Col key={key} xs={12} sm={8} md={4}>
              <AutoComplete
                size="small"
                allowClear
                style={{ width: '100%' }}
                placeholder={label}
                value={filtros[key]}
                options={opcionesFiltro[key]}
                // `filterOption` con nuestra normalización: sin esto el
                // desplegable compara con acentos y escribir "prenada" no
                // ofrece "Preñadas", aunque el filtro de la tabla sí la
                // encuentre. Los dos tienen que coincidir.
                filterOption={(entrada, opcion) =>
                  normalizar(opcion?.value).includes(normalizar(entrada))}
                onChange={(v) => setFiltros((prev) => ({ ...prev, [key]: v ?? '' }))}
              />
            </Col>
          ))}
          <Col xs={24} md={4}>
            {hayFiltro ? (
              <Space size={8}>
                <Button size="small" onClick={() => setFiltros(FILTROS_VACIOS)}>Limpiar</Button>
                {/* Se dice que los cargados quedaron afuera: si sólo mostrara
                    el número, el operador ve menos filas de las que espera y no
                    sabe si es el filtro o si le falta cargar. */}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {totalFiltrado} sin cargar
                  {ocultosPorCargado > 0
                    && ` · ${ocultosPorCargado} ya cargado${ocultosPorCargado === 1 ? '' : 's'}`}
                </Text>
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{total} animales</Text>
            )}
          </Col>
        </Row>
      )}

      {!loading && eventoId && total > 0 && (
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={treeData}
          columns={columns}
          rowKey={(r) => (r.isGroup ? `corral-${r.corral}` : `a-${r.id_animal}`)}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
          expandable={{ defaultExpandAllRows: false }}
          locale={{ emptyText: hayFiltro ? 'Ningún animal coincide con el filtro' : 'Sin animales' }}
        />
      )}

      {/* ── Ventas cargadas, sólo lectura ──
          La grilla de arriba lista TODOS los animales del evento con sus campos
          editables; esto es el recorte de lo que efectivamente se vendió, para
          leerlo o mostrarlo sin riesgo de tocar un dato. */}
      {!loading && eventoId && ventasCargadas.length > 0 && (
        <Card
          size="small"
          style={{ marginTop: 24 }}
          title={
            <span>
              Ventas cargadas{' '}
              <Text type="secondary" style={{ fontWeight: 400 }}>
                ({ventasCargadas.length} de {total} animales)
              </Text>
            </span>
          }
        >
          <Table
            scroll={{ x: 'max-content' }}
            dataSource={ventasCargadas}
            rowKey={(r) => `v-${r.id_animal}`}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true, hideOnSinglePage: true }}
            columns={[
              { title: 'Animal', dataIndex: 'nom_animal', width: 200, ellipsis: true },
              // Pegada al nombre, no después del corral: se lee "qué animal y
              // de qué categoría" de un tirón, que es como se controla la
              // planilla contra el catálogo.
              { title: 'Categoría', dataIndex: 'nom_categoria', ellipsis: true, width: 200, render: (v) => v || '—' },
              { title: 'Corral', dataIndex: 'corral', align: 'right', width: 80, render: (v) => v ?? '—' },
              { title: 'Comprador', dataIndex: 'comprador_texto', ellipsis: true, width: 200 },
              {
                title: '%', dataIndex: 'porcentaje_venta', align: 'right', width: 70,
                render: (v) => `${Number(v ?? 100)}%`,
              },
              { title: 'Monto', dataIndex: 'importe_venta', align: 'right', width: 130, render: money },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default GrillaTransitoria;
